  // Mapbox configuration - Optimized for free tier usage
export const MAPBOX_CONFIG = {
  // You'll need to get your own Mapbox access token from https://mapbox.com
  // For now, using a placeholder - replace with your actual token
  accessToken: process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || 'pk.eyJ1IjoiZXhhbXBsZSIsImEiOiJjbGV4YW1wbGUifQ.example',
  
  // Map Styles with Dynamic Lighting Support
  mapStyles: {
    'cemetery-custom': 'mapbox://styles/jayrawrrr/cmfijs9ze004j01qyc62wd4uq', // Your custom cemetery style (latest version)
    'mapbox-standard': 'mapbox://styles/mapbox/standard' // Mapbox Standard with dynamic lighting presets
  },
  
  // Default style - using Mapbox Standard for dynamic lighting
  defaultMapStyle: 'mapbox://styles/mapbox/standard',
  
  // Cemetery map bounds (Garden of Memories Memorial Park, Pateros)
  cemeteryBounds: {
    north: 14.5385,  // Garden of Memories northern boundary
    south: 14.5375,  // Garden of Memories southern boundary
    east: 121.0715,  // Garden of Memories eastern boundary
    west: 121.0690   // Garden of Memories western boundary
  },
  
  // Default viewport (Garden of Memories Memorial Park)
  defaultViewport: {
    latitude: 14.538417451134988,   // Garden of Memories Memorial Park center
    longitude: 121.07088545636164, // Garden of Memories Memorial Park center
    zoom: 17.5  // Shows approximately 50 meters
  },
  
  // FREE FEATURES: Custom markers using Mapbox GL JS (no additional cost)
  lotMarkers: {
    available: {
      color: '#28a745',
      size: 12,
      symbol: 'circle',
      // Using free Mapbox icons
      icon: 'marker-15'
    },
    reserved: {
      color: '#ffc107',
      size: 12,
      symbol: 'circle',
      icon: 'marker-15'
    },
    occupied: {
      color: '#dc3545',
      size: 12,
      symbol: 'circle',
      icon: 'marker-15'
    },
    landmark: {
      color: '#6c757d',
      size: 16,
      symbol: 'marker',
      icon: 'religious-christian-15' // FREE Mapbox icon
    }
  },
  
  // FREE FEATURES: Geocoding configuration (50,000 free requests/month)
  geocoding: {
    enabled: true,
    // Using free Mapbox Geocoding API
    apiUrl: 'https://api.mapbox.com/geocoding/v5/mapbox.places',
    // Cemetery location for context (Garden of Memories, Pateros)
    proximity: [121.070, 14.538], // lng, lat
    country: 'PH', // Philippines
    types: 'address,poi'
  },
  
  // FREE FEATURES: Directions API (25,000 free requests/month)
  directions: {
    enabled: true,
    // Using free Mapbox Directions API
    apiUrl: 'https://api.mapbox.com/directions/v5/mapbox',
    profile: 'walking', // walking, driving, cycling
    alternatives: false, // Save on API calls
    steps: true,
    geometries: 'geojson'
  },
  
  // FREE FEATURES: Map controls and interactions
  controls: {
    navigation: true, // FREE - zoom/pan controls
    scale: true, // FREE - scale control
    fullscreen: true, // FREE - fullscreen toggle
    geolocate: true, // FREE - user location
    attribution: true // FREE - required attribution
  },
  
  // FREE FEATURES: Clustering for performance (no additional cost)
  clustering: {
    enabled: true,
    radius: 50,
    maxZoom: 14,
    minPoints: 2
  },
  
  // Cemetery internal navigation features
  internalNavigation: {
    enabled: true,
    // Main pathways within Garden of Memories (actual coordinates)
    pathways: [
      {
        name: "Main Entrance Path",
        coordinates: [
          [121.068655, 14.538215], // Main Building
          [121.071879, 14.538307], // Restroom 1
          [121.0712586366533, 14.537806991461068]   // Chapel
        ],
        type: "main"
      },
      {
        name: "Chapel to Columbarium Path",
        coordinates: [
          [121.0712586366533, 14.537806991461068],  // Chapel
          [121.0691971461874, 14.537855022845381]   // Columbarium
        ],
        type: "section"
      },
      {
        name: "Office to Columbarium Path", 
        coordinates: [
          [121.071879, 14.538307], // Restroom 1
          [121.0691971461874, 14.537855022845381]   // Columbarium
        ],
        type: "section"
      }
    ],
        // Key landmarks within the cemetery (actual coordinates)
        landmarks: [
          {
            name: "Main Chapel",
            coordinates: [121.0712586366533, 14.537806991461068],
            type: "chapel"
          },
          {
            name: "Entrance",
            coordinates: [121.06912990201427, 14.53814713981777],
            type: "entrance"
          },
          {
            name: "Office",
            coordinates: [121.06920165712648, 14.538409566278938],
            type: "office"
          },
          {
            name: "Columbarium",
            coordinates: [121.0691971461874, 14.537855022845381],
            type: "columbarium"
          }
        ]
  }
};


// Helper function to get lighting preset for Mapbox Standard
export const getLightingPresetForTimeOfDay = (timeOfDay) => {
  const lightingPresets = {
    'dawn': 'dawn',
    'day': 'day', 
    'dusk': 'dusk',
    'night': 'night'
  };
  
  return lightingPresets[timeOfDay] || 'day';
};

// Helper function to get map style (always returns Mapbox Standard for dynamic lighting)
export const getMapStyleForTimeOfDay = (timeOfDay) => {
  return MAPBOX_CONFIG.mapStyles['mapbox-standard'];
};

// Helper function to convert image coordinates to lat/lng
export const imageToLatLng = (imageX, imageY, imageWidth = 2048, imageHeight = 1025) => {
  // Convert image coordinates to normalized coordinates (0-1)
  const normalizedX = imageX / imageWidth;
  const normalizedY = imageY / imageHeight;
  
  // Convert to lat/lng using cemetery bounds
  const lng = MAPBOX_CONFIG.cemeteryBounds.west + 
    (normalizedX * (MAPBOX_CONFIG.cemeteryBounds.east - MAPBOX_CONFIG.cemeteryBounds.west));
  const lat = MAPBOX_CONFIG.cemeteryBounds.north - 
    (normalizedY * (MAPBOX_CONFIG.cemeteryBounds.north - MAPBOX_CONFIG.cemeteryBounds.south));
  
  return [lat, lng];
};

// Helper function to convert lat/lng to image coordinates
export const latLngToImage = (lat, lng, imageWidth = 2048, imageHeight = 1025) => {
  // Convert lat/lng to normalized coordinates
  const normalizedX = (lng - MAPBOX_CONFIG.cemeteryBounds.west) / 
    (MAPBOX_CONFIG.cemeteryBounds.east - MAPBOX_CONFIG.cemeteryBounds.west);
  const normalizedY = (MAPBOX_CONFIG.cemeteryBounds.north - lat) / 
    (MAPBOX_CONFIG.cemeteryBounds.north - MAPBOX_CONFIG.cemeteryBounds.south);
  
  // Convert to image coordinates
  const imageX = normalizedX * imageWidth;
  const imageY = normalizedY * imageHeight;
  
  return [imageX, imageY];
};

// FREE FEATURE: Geocoding utility (50,000 free requests/month)
export const geocodeAddress = async (address, accessToken) => {
  if (!MAPBOX_CONFIG.geocoding.enabled) return null;
  
  try {
    const { apiUrl, proximity, country, types } = MAPBOX_CONFIG.geocoding;
    const url = `${apiUrl}/${encodeURIComponent(address)}.json?` +
      `access_token=${accessToken}&` +
      `proximity=${proximity[0]},${proximity[1]}&` +
      `country=${country}&` +
      `types=${types}&` +
      `limit=5`;
    
    const response = await fetch(url);
    const data = await response.json();
    
    return data.features || [];
  } catch (error) {
    console.error('Geocoding error:', error);
    return [];
  }
};

// FREE FEATURE: Reverse geocoding utility
export const reverseGeocode = async (lng, lat, accessToken) => {
  if (!MAPBOX_CONFIG.geocoding.enabled) return null;
  
  try {
    const { apiUrl, country, types } = MAPBOX_CONFIG.geocoding;
    const url = `${apiUrl}/${lng},${lat}.json?` +
      `access_token=${accessToken}&` +
      `country=${country}&` +
      `types=${types}&` +
      `limit=1`;
    
    const response = await fetch(url);
    const data = await response.json();
    
    return data.features?.[0] || null;
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    return null;
  }
};

// FREE FEATURE: Directions utility (25,000 free requests/month)
export const getDirections = async (start, end, accessToken, profile = 'walking') => {
  if (!MAPBOX_CONFIG.directions.enabled) return null;
  
  try {
    const { apiUrl, alternatives, steps, geometries } = MAPBOX_CONFIG.directions;
    const startCoords = `${start[0]},${start[1]}`;
    const endCoords = `${end[0]},${end[1]}`;
    
    const url = `${apiUrl}/${profile}/${startCoords};${endCoords}?` +
      `access_token=${accessToken}&` +
      `alternatives=${alternatives}&` +
      `steps=${steps}&` +
      `geometries=${geometries}`;
    
    const response = await fetch(url);
    const data = await response.json();
    
    return data.routes?.[0] || null;
  } catch (error) {
    console.error('Directions error:', error);
    return null;
  }
};

// FREE FEATURE: Distance calculation utility
export const calculateDistance = (coord1, coord2) => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (coord2[1] - coord1[1]) * Math.PI / 180;
  const dLon = (coord2[0] - coord1[0]) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(coord1[1] * Math.PI / 180) * Math.cos(coord2[1] * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c; // Distance in kilometers
};

// FREE FEATURE: Map style switcher

// FREE FEATURE: Optimization API (100,000 free requests/month)
export const optimizeRoute = async (waypoints, accessToken, profile = 'walking') => {
  try {
    const coordinates = waypoints.map(wp => `${wp[0]},${wp[1]}`).join(';');
    const url = `https://api.mapbox.com/optimized-trips/v1/mapbox/${profile}/${coordinates}?` +
      `access_token=${accessToken}&` +
      `source=first&` +
      `destination=last&` +
      `roundtrip=true`;
    
    const response = await fetch(url);
    const data = await response.json();
    
    return data.trips?.[0] || null;
  } catch (error) {
    console.error('Route optimization error:', error);
    return null;
  }
};

// FREE FEATURE: Matrix API (100,000 free elements/month)
export const getMatrix = async (sources, destinations, accessToken, profile = 'walking') => {
  try {
    const sourceCoords = sources.map(s => `${s[0]},${s[1]}`).join(';');
    const destCoords = destinations.map(d => `${d[0]},${d[1]}`).join(';');
    
    const url = `https://api.mapbox.com/distance-matrix/v1/mapbox/${profile}/${sourceCoords};${destCoords}?` +
      `access_token=${accessToken}&` +
      `sources=0&` +
      `destinations=1`;
    
    const response = await fetch(url);
    const data = await response.json();
    
    return data;
  } catch (error) {
    console.error('Matrix API error:', error);
    return null;
  }
};

// FREE FEATURE: Isochrone API (100,000 free requests/month)
export const getIsochrone = async (coordinates, minutes, profile = 'walking', accessToken) => {
  try {
    const url = `https://api.mapbox.com/isochrone/v1/mapbox/${profile}/${coordinates[0]},${coordinates[1]}?` +
      `access_token=${accessToken}&` +
      `contours_minutes=${minutes}&` +
      `polygons=true`;
    
    const response = await fetch(url);
    const data = await response.json();
    
    return data.features || [];
  } catch (error) {
    console.error('Isochrone API error:', error);
    return [];
  }
};

// FREE FEATURE: Static Images API (50,000 free requests/month)
export const generateStaticMap = (coordinates, zoom = 15, width = 600, height = 400, accessToken) => {
  const [lng, lat] = coordinates;
  return `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/` +
    `pin-s-lot+ff0000(${lng},${lat})/${lng},${lat},${zoom},0/${width}x${height}@2x?` +
    `access_token=${accessToken}`;
};

// FREE FEATURE: Tilequery API (100,000 free requests/month)
export const tilequery = async (coordinates, tilesetId, accessToken, radius = 1000) => {
  try {
    const url = `https://api.mapbox.com/v4/${tilesetId}/tilequery/${coordinates[0]},${coordinates[1]}.json?` +
      `access_token=${accessToken}&` +
      `radius=${radius}&` +
      `limit=10`;
    
    const response = await fetch(url);
    const data = await response.json();
    
    return data.features || [];
  } catch (error) {
    console.error('Tilequery API error:', error);
    return [];
  }
};

// FREE FEATURE: Map Matching API (100,000 free requests/month)
export const mapMatch = async (coordinates, accessToken, profile = 'walking') => {
  try {
    const coordString = coordinates.map(c => `${c[0]},${c[1]}`).join(';');
    const url = `https://api.mapbox.com/matching/v5/mapbox/${profile}/${coordString}?` +
      `access_token=${accessToken}&` +
      `geometries=geojson&` +
      `overview=full`;
    
    const response = await fetch(url);
    const data = await response.json();
    
    return data.matchings?.[0] || null;
  } catch (error) {
    console.error('Map matching error:', error);
    return null;
  }
};

// FREE FEATURE: Search Box API utilities
export const createSearchBox = (map, accessToken, options = {}) => {
  // Note: SearchBox and AddressAutofill require mapbox-gl to be imported
  // These are utility functions that should be used where mapbox-gl is available
  const defaultOptions = {
    accessToken,
    map,
    marker: false,
    placeholder: 'Search for lots, landmarks...',
    proximity: [121.070, 14.538], // Cemetery center (Garden of Memories, Pateros)
    country: 'PH',
    ...options
  };
  
  // Return configuration object instead of instantiated class
  return {
    type: 'SearchBox',
    options: defaultOptions
  };
};

// FREE FEATURE: Address Autofill utilities
export const createAddressAutofill = (accessToken, options = {}) => {
  const defaultOptions = {
    accessToken,
    options: {
      country: 'PH',
      language: 'en',
      ...options
    }
  };
  
  // Return configuration object instead of instantiated class
  return {
    type: 'AddressAutofill',
    options: defaultOptions
  };
};

// Enhanced pathfinding utilities using your GeoJSON road network
export const findNearestRoadPoint = (point, roadGraph) => {
  if (!roadGraph || !(roadGraph instanceof RoadGraph)) {
    console.error('Invalid road graph provided');
    return null;
  }
  
  return roadGraph.findNearestNode(point);
};

export const findRoadContainingPoint = (point, roadNetwork) => {
  // Find which road segment is closest to the given point
  let nearestRoad = null;
  let minDistance = Infinity;
  
  roadNetwork.features.forEach(road => {
    road.geometry.coordinates.forEach(coord => {
      const distance = calculateDistance(point, coord);
      if (distance < minDistance) {
        minDistance = distance;
        nearestRoad = road;
      }
    });
  });
  
  return nearestRoad;
};

export const findPathThroughRoads = (startPoint, endPoint, roadGraph) => {
  if (!roadGraph || !(roadGraph instanceof RoadGraph)) {
    console.error('Invalid road graph provided');
    return [startPoint, endPoint]; // Fallback to direct line
  }
  
  // Use Dijkstra's algorithm to find shortest path
  const result = roadGraph.findShortestPath(startPoint, endPoint);
  
  if (result && result.path.length > 0) {
    return result.path;
  }
  
  // Fallback: direct line if no path found
    return [startPoint, endPoint];
};

// Enhanced grave routing using your GeoJSON road network
export const calculateRouteToGrave = (startPoint, targetGrave, roadGraph) => {
  if (!roadGraph || !(roadGraph instanceof RoadGraph)) {
    console.error('Invalid road graph provided');
    return null;
  }
  
  // Find nearest road points for start and end
  const startRoadInfo = findNearestRoadPoint(startPoint, roadGraph);
  const endRoadInfo = findNearestRoadPoint(targetGrave.coordinates, roadGraph);
  
  if (!startRoadInfo || !endRoadInfo) {
    console.error('Could not find nearest road points');
    return null;
  }
  
  // Calculate route through road network using Dijkstra's algorithm
  const routePath = findPathThroughRoads(startPoint, targetGrave.coordinates, roadGraph);
  
  // Get road information for the path
  const roadInfo = roadGraph.getRoadInfo(routePath);
  
  // Generate cemetery-specific instructions
  const instructions = generateCemeteryInstructions(startPoint, targetGrave, routePath, roadGraph, roadInfo);
  
  return {
    waypoints: routePath,
    instructions,
    totalDistance: calculateTotalDistance(routePath),
    estimatedTime: Math.ceil(calculateTotalDistance(routePath) / 50), // 50m per minute walking
    destination: targetGrave,
    roadInfo: roadInfo,
    startNode: startRoadInfo.node,
    endNode: endRoadInfo.node
  };
};

// Generate cemetery-specific navigation instructions using road graph
export const generateCemeteryInstructions = (startPoint, targetGrave, routePath, roadGraph, roadInfo) => {
  const instructions = [
    {
      type: 'start',
      instruction: `Start at current location`,
      distance: 0,
      icon: '📍'
    }
  ];
  
  // Add road-specific instructions based on actual road segments
  if (roadInfo && roadInfo.length > 0) {
    roadInfo.forEach((segment, index) => {
      const road = segment.road;
      const distance = segment.distance;
      
      instructions.push({
        type: 'road',
        instruction: `Follow ${road.name}`,
        distance: distance,
        icon: '🛣️',
        road: road
      });
    });
  }
  
  // Add landmark-based instructions using your complete GeoJSON landmarks
  const landmarks = [
    { name: "Chapel", coordinates: [121.071317, 14.537649] },
    { name: "Main Building", coordinates: [121.068655, 14.538215] },
    { name: "Columbarium", coordinates: [121.069129, 14.538037] },
    { name: "Restroom 1", coordinates: [121.071879, 14.538307] },
    { name: "Restroom 2", coordinates: [121.072004, 14.538719] },
    { name: "Restroom 3", coordinates: [121.071708, 14.539057] }
  ];
  const targetGarden = targetGrave.garden || 'Unknown';
  
  // Check if route passes through landmarks
  landmarks.forEach(landmark => {
    const landmarkDistance = calculateDistance(startPoint, landmark.coordinates);
    const graveDistance = calculateDistance(startPoint, targetGrave.coordinates);
    
    if (landmarkDistance < graveDistance && landmarkDistance < 100) { // Within 100m
      instructions.push({
        type: 'landmark',
        instruction: `Pass by ${landmark.name}`,
        distance: landmarkDistance,
        icon: getLandmarkIcon(landmark.type),
        landmark: landmark
      });
    }
  });
  
  // Add garden-specific instructions
  instructions.push({
    type: 'garden',
    instruction: `Head towards Garden ${targetGarden}`,
    distance: calculateDistance(startPoint, targetGrave.coordinates) * 0.7,
    icon: '🌳'
  });
  
  // Add grave-specific instructions
  instructions.push({
    type: 'navigate',
    instruction: `Look for Grave ${targetGrave.id} in Row ${targetGrave.row}, Column ${targetGrave.column}`,
    distance: calculateDistance(startPoint, targetGrave.coordinates) * 0.9,
    icon: '🔍'
  });
  
  instructions.push({
    type: 'arrive',
    instruction: `Arrive at Grave ${targetGrave.id}`,
    distance: calculateTotalDistance(routePath),
    icon: '🪦'
  });
  
  return instructions;
};

// Get appropriate icon for landmark type
export const getLandmarkIcon = (landmarkType) => {
  const icons = {
    'chapel': '⛪',
    'entrance': '🚪',
    'office': '🏢',
    'columbarium': '🏛️',
    'default': '📍'
  };
  return icons[landmarkType] || icons.default;
};

export const calculateTotalDistance = (path) => {
  let totalDistance = 0;
  for (let i = 0; i < path.length - 1; i++) {
    totalDistance += calculateDistance(path[i], path[i + 1]);
  }
  return totalDistance;
};

// Advanced grave location and routing features using your GeoJSON data

// Road Graph Implementation from your GeoJSON linestrings
export class RoadGraph {
  constructor(roadNetwork) {
    this.nodes = new Map(); // coordinate -> node data
    this.edges = new Map(); // nodeId -> connected nodes
    this.roadSegments = new Map(); // roadId -> road data
    this.gardenCenters = new Map(); // gardenId -> center coordinates
    this.buildGraph(roadNetwork);
    this.addGardenConnections(roadNetwork);
  }

  buildGraph(roadNetwork) {
    // Process each road feature from your complete GeoJSON
    roadNetwork.features.forEach((road, roadIndex) => {
      const roadId = road.id || road.properties.roadId || `road-${roadIndex}`;
      const coordinates = road.geometry.coordinates;
      
      this.roadSegments.set(roadId, {
        id: roadId,
        name: road.properties.name || `Road ${roadIndex}`,
        type: road.properties.type || 'pathway',
        coordinates: coordinates
      });

      // Create nodes for each coordinate point
      coordinates.forEach((coord, coordIndex) => {
        const nodeId = this.getNodeId(coord);
        
        if (!this.nodes.has(nodeId)) {
          this.nodes.set(nodeId, {
            id: nodeId,
            coordinates: coord,
            roads: new Set(),
            connections: new Set()
          });
        }

        const node = this.nodes.get(nodeId);
        node.roads.add(roadId);

        // Connect to adjacent nodes in the same road
        if (coordIndex > 0) {
          const prevCoord = coordinates[coordIndex - 1];
          const prevNodeId = this.getNodeId(prevCoord);
          this.connectNodes(nodeId, prevNodeId, roadId);
        }
      });
    });

    // Connect nodes at intersections (same coordinates)
    this.connectIntersections();
  }

  getNodeId(coord) {
    // Create unique node ID from coordinates (rounded to avoid floating point issues)
    const [lng, lat] = coord;
    return `${lng.toFixed(6)},${lat.toFixed(6)}`;
  }

  connectNodes(nodeId1, nodeId2, roadId) {
    if (!this.edges.has(nodeId1)) {
      this.edges.set(nodeId1, new Set());
    }
    if (!this.edges.has(nodeId2)) {
      this.edges.set(nodeId2, new Set());
    }

    this.edges.get(nodeId1).add({
      nodeId: nodeId2,
      roadId: roadId,
      distance: this.calculateDistance(this.nodes.get(nodeId1).coordinates, this.nodes.get(nodeId2).coordinates)
    });

    this.edges.get(nodeId2).add({
      nodeId: nodeId1,
      roadId: roadId,
      distance: this.calculateDistance(this.nodes.get(nodeId1).coordinates, this.nodes.get(nodeId2).coordinates)
    });
  }

  connectIntersections() {
    // Find nodes with same coordinates (intersections)
    const coordGroups = new Map();
    
    this.nodes.forEach((node, nodeId) => {
      const coordKey = `${node.coordinates[0].toFixed(6)},${node.coordinates[1].toFixed(6)}`;
      if (!coordGroups.has(coordKey)) {
        coordGroups.set(coordKey, []);
      }
      coordGroups.get(coordKey).push(nodeId);
    });

    // Connect nodes at intersections
    coordGroups.forEach((nodeIds) => {
      if (nodeIds.length > 1) {
        for (let i = 0; i < nodeIds.length; i++) {
          for (let j = i + 1; j < nodeIds.length; j++) {
            this.connectNodes(nodeIds[i], nodeIds[j], 'intersection');
          }
        }
      }
    });
  }

  addGardenConnections(roadNetwork) {
    // Extract garden polygons and create center points
    const gardens = roadNetwork.features.filter(feature => feature.properties.type === 'garden');
    
    gardens.forEach(garden => {
      const gardenId = garden.properties.id;
      const coordinates = garden.geometry.coordinates[0]; // Get outer ring
      
      // Calculate garden center
      const centerLng = coordinates.reduce((sum, coord) => sum + coord[0], 0) / coordinates.length;
      const centerLat = coordinates.reduce((sum, coord) => sum + coord[1], 0) / coordinates.length;
      const centerCoord = [centerLng, centerLat];
      
      this.gardenCenters.set(gardenId, centerCoord);
      
      // Create garden center node
      const gardenNodeId = this.getNodeId(centerCoord);
      if (!this.nodes.has(gardenNodeId)) {
        this.nodes.set(gardenNodeId, {
          id: gardenNodeId,
          coordinates: centerCoord,
          roads: new Set(),
          connections: new Set(),
          isGarden: true,
          gardenId: gardenId
        });
      }
      
      // Find the nearest road node to connect to
      let nearestNodeId = null;
      let minDistance = Infinity;
      
      this.nodes.forEach((node, nodeId) => {
        if (!node.isGarden) { // Don't connect to other gardens
          const distance = this.calculateDistance(centerCoord, node.coordinates);
          if (distance < minDistance) {
            minDistance = distance;
            nearestNodeId = nodeId;
          }
        }
      });
      
      // Connect garden center to nearest road node (increased distance threshold)
      if (nearestNodeId && minDistance < 0.01) { // Increased threshold to 0.01 degrees (~1km)
        this.connectNodes(gardenNodeId, nearestNodeId, `garden-${gardenId}-connection`);
        
        // Add this as a road segment for navigation
        const connectionRoadId = `garden-${gardenId}-path`;
        this.roadSegments.set(connectionRoadId, {
          id: connectionRoadId,
          name: `Path to ${garden.properties.name}`,
          type: 'garden-path',
          coordinates: [this.nodes.get(nearestNodeId).coordinates, centerCoord]
        });
        
        console.log(`Connected ${garden.properties.name} to road network:`, {
          gardenId: gardenId,
          centerCoord: centerCoord,
          nearestNodeId: nearestNodeId,
          nearestNodeCoord: this.nodes.get(nearestNodeId).coordinates,
          distance: minDistance
        });
      } else {
        console.warn(`Could not connect ${garden.properties.name} to road network:`, {
          gardenId: gardenId,
          centerCoord: centerCoord,
          minDistance: minDistance,
          nearestNodeId: nearestNodeId
        });
      }
    });
    
    // Add additional pathway segments to improve connectivity
    this.addAdditionalPathways();
  }

  addAdditionalPathways() {
    // Add pathways between gardens and main roads for better connectivity
    const additionalPathways = [
      {
        name: "Garden A Access Path",
        coordinates: [
          [121.068655, 14.538215], // Main Building
          [121.0696515, 14.5385915]  // Garden A center
        ],
        type: "access-path"
      },
      {
        name: "Garden B Access Path", 
        coordinates: [
          [121.068655, 14.538215], // Main Building
          [121.0702695, 14.5385845]  // Garden B center
        ],
        type: "access-path"
      },
      {
        name: "Garden C Access Path",
        coordinates: [
          [121.071317, 14.537649], // Chapel
          [121.071278, 14.5382515]  // Garden C center
        ],
        type: "access-path"
      }
    ];

    additionalPathways.forEach((pathway, index) => {
      const pathwayId = `additional-path-${index}`;
      this.roadSegments.set(pathwayId, {
        id: pathwayId,
        name: pathway.name,
        type: pathway.type,
        coordinates: pathway.coordinates
      });

      // Create nodes for pathway endpoints
      pathway.coordinates.forEach((coord, coordIndex) => {
        const nodeId = this.getNodeId(coord);
        if (!this.nodes.has(nodeId)) {
          this.nodes.set(nodeId, {
            id: nodeId,
            coordinates: coord,
            roads: new Set(),
            connections: new Set()
          });
        }

        // Connect to adjacent nodes
        if (coordIndex > 0) {
          const prevCoord = pathway.coordinates[coordIndex - 1];
          const prevNodeId = this.getNodeId(prevCoord);
          this.connectNodes(nodeId, prevNodeId, pathwayId);
        }
      });
    });
  }

  calculateDistance(coord1, coord2) {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (coord2[1] - coord1[1]) * Math.PI / 180;
    const dLon = (coord2[0] - coord1[0]) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(coord1[1] * Math.PI / 180) * Math.cos(coord2[1] * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c * 1000; // Distance in meters
  }

  findNearestNode(point) {
    let nearestNode = null;
    let minDistance = Infinity;

    this.nodes.forEach((node) => {
      const distance = this.calculateDistance(point, node.coordinates);
      if (distance < minDistance) {
        minDistance = distance;
        nearestNode = node;
      }
    });

    return { node: nearestNode, distance: minDistance };
  }

  // Dijkstra's algorithm for shortest path
  findShortestPath(startPoint, endPoint) {
    const startNode = this.findNearestNode(startPoint).node;
    const endNode = this.findNearestNode(endPoint).node;

    if (!startNode || !endNode) {
      return null;
    }

    const distances = new Map();
    const previous = new Map();
    const unvisited = new Set();

    // Initialize distances
    this.nodes.forEach((node) => {
      distances.set(node.id, node.id === startNode.id ? 0 : Infinity);
      unvisited.add(node.id);
    });

    while (unvisited.size > 0) {
      // Find unvisited node with smallest distance
      let currentNode = null;
      let minDistance = Infinity;
      
      unvisited.forEach((nodeId) => {
        if (distances.get(nodeId) < minDistance) {
          minDistance = distances.get(nodeId);
          currentNode = nodeId;
        }
      });

      if (currentNode === endNode.id) {
        break; // Reached destination
      }

      unvisited.delete(currentNode);

      // Check neighbors
      const neighbors = this.edges.get(currentNode) || new Set();
      neighbors.forEach((edge) => {
        const alt = distances.get(currentNode) + edge.distance;
        if (alt < distances.get(edge.nodeId)) {
          distances.set(edge.nodeId, alt);
          previous.set(edge.nodeId, currentNode);
        }
      });
    }

    // Reconstruct path
    const path = [];
    let current = endNode.id;
    while (current !== undefined) {
      const node = this.nodes.get(current);
      path.unshift(node.coordinates);
      current = previous.get(current);
    }

    return {
      path: path,
      distance: distances.get(endNode.id),
      startNode: startNode,
      endNode: endNode
    };
  }

  // Get road information for a path
  getRoadInfo(path) {
    const roadInfo = [];
    
    for (let i = 0; i < path.length - 1; i++) {
      const currentCoord = path[i];
      const nextCoord = path[i + 1];
      
      // Find which road connects these coordinates
      const currentNode = this.nodes.get(this.getNodeId(currentCoord));
      const nextNode = this.nodes.get(this.getNodeId(nextCoord));
      
      if (currentNode && nextNode) {
        const edges = this.edges.get(currentNode.id) || new Set();
        edges.forEach((edge) => {
          if (edge.nodeId === nextNode.id && edge.roadId !== 'intersection') {
            const road = this.roadSegments.get(edge.roadId);
            if (road) {
              roadInfo.push({
                road: road,
                distance: edge.distance,
                coordinates: [currentCoord, nextCoord]
              });
            }
          }
        });
      }
    }
    
    return roadInfo;
  }
}

// Multi-grave route optimization
export const optimizeMultiGraveRoute = (startPoint, graves, roadNetwork) => {
  if (graves.length === 0) return null;
  if (graves.length === 1) return calculateRouteToGrave(startPoint, graves[0], roadNetwork);
  
  // Calculate distances from start to each grave
  const distances = graves.map(grave => ({
    grave,
    distance: calculateDistance(startPoint, grave.coordinates)
  }));
  
  // Sort by distance for simple optimization
  distances.sort((a, b) => a.distance - b.distance);
  
  // Build optimized route
  const optimizedRoute = {
    waypoints: [startPoint],
    instructions: [],
    totalDistance: 0,
    estimatedTime: 0,
    graves: distances.map(d => d.grave)
  };
  
  let currentPoint = startPoint;
  distances.forEach(({ grave }, index) => {
    const route = calculateRouteToGrave(currentPoint, grave, roadNetwork);
    optimizedRoute.waypoints.push(...route.waypoints.slice(1)); // Skip first point to avoid duplication
    optimizedRoute.instructions.push(...route.instructions);
    optimizedRoute.totalDistance += route.totalDistance;
    optimizedRoute.estimatedTime += route.estimatedTime;
    currentPoint = grave.coordinates;
  });
  
  return optimizedRoute;
};

// Find graves within walking distance
export const findGravesWithinDistance = (centerPoint, maxDistance, allGraves, roadNetwork) => {
  return allGraves.filter(grave => {
    const route = calculateRouteToGrave(centerPoint, grave, roadNetwork);
    return route.totalDistance <= maxDistance;
  });
};

// Calculate route alternatives
export const calculateRouteAlternatives = (startPoint, endPoint, roadNetwork) => {
  const directRoute = findPathThroughRoads(startPoint, endPoint, roadNetwork);
  
  // Alternative 1: Via landmarks from your complete GeoJSON data
  const landmarks = [
    { name: "Chapel", coordinates: [121.071317, 14.537649] },
    { name: "Main Building", coordinates: [121.068655, 14.538215] },
    { name: "Columbarium", coordinates: [121.069129, 14.538037] },
    { name: "Restroom 1", coordinates: [121.071879, 14.538307] },
    { name: "Restroom 2", coordinates: [121.072004, 14.538719] },
    { name: "Restroom 3", coordinates: [121.071708, 14.539057] }
  ];
  
  const viaLandmarkRoutes = landmarks.map(landmark => {
    const route1 = findPathThroughRoads(startPoint, landmark.coordinates, roadNetwork);
    const route2 = findPathThroughRoads(landmark.coordinates, endPoint, roadNetwork);
    return {
      name: `Via ${landmark.name}`,
      waypoints: [...route1, ...route2.slice(1)],
      totalDistance: calculateTotalDistance([...route1, ...route2.slice(1)]),
      estimatedTime: Math.ceil(calculateTotalDistance([...route1, ...route2.slice(1)]) / 50),
      viaLandmark: landmark
    };
  });
  
  return {
    direct: {
      name: 'Direct Route',
      waypoints: directRoute,
      totalDistance: calculateTotalDistance(directRoute),
      estimatedTime: Math.ceil(calculateTotalDistance(directRoute) / 50)
    },
    alternatives: viaLandmarkRoutes
  };
};

// Grave-to-grave navigation with cemetery context
export const navigateBetweenGraves = (startGrave, endGrave, roadNetwork) => {
  const startPoint = startGrave.coordinates;
  const endPoint = endGrave.coordinates;
  
  // Calculate route through road network
  const routePath = findPathThroughRoads(startPoint, endPoint, roadNetwork);
  
  // Generate cemetery-specific instructions
  const instructions = [
    {
      type: 'start',
      instruction: `Start at Grave ${startGrave.id}`,
      distance: 0,
      icon: '🪦'
    }
  ];
  
  // Add garden transition instructions if different gardens
  if (startGrave.garden !== endGrave.garden) {
    instructions.push({
      type: 'transition',
      instruction: `Move from Garden ${startGrave.garden} to Garden ${endGrave.garden}`,
      distance: calculateTotalDistance(routePath) * 0.5,
      icon: '🌳'
    });
  }
  
  instructions.push({
    type: 'navigate',
    instruction: `Look for Grave ${endGrave.id} in Row ${endGrave.row}, Column ${endGrave.column}`,
    distance: calculateTotalDistance(routePath) * 0.8,
    icon: '🔍'
  });
  
  instructions.push({
    type: 'arrive',
    instruction: `Arrive at Grave ${endGrave.id}`,
    distance: calculateTotalDistance(routePath),
    icon: '🪦'
  });
  
  return {
    waypoints: routePath,
    instructions,
    totalDistance: calculateTotalDistance(routePath),
    estimatedTime: Math.ceil(calculateTotalDistance(routePath) / 50),
    startGrave,
    endGrave
  };
};

// Interactive route planning with waypoints
export const planInteractiveRoute = (waypoints, roadNetwork) => {
  if (waypoints.length < 2) return null;
  
  const route = {
    waypoints: [],
    instructions: [],
    totalDistance: 0,
    estimatedTime: 0,
    segments: []
  };
  
  for (let i = 0; i < waypoints.length - 1; i++) {
    const segment = findPathThroughRoads(waypoints[i], waypoints[i + 1], roadNetwork);
    route.waypoints.push(...segment);
    route.segments.push(segment);
    route.totalDistance += calculateTotalDistance(segment);
    route.estimatedTime += Math.ceil(calculateTotalDistance(segment) / 50);
    
    // Add segment instructions
    route.instructions.push({
      type: 'segment',
      instruction: `Navigate to waypoint ${i + 2}`,
      distance: calculateTotalDistance(segment),
      icon: '🚶'
    });
  }
  
  return route;
};

// Advanced routing using your custom style's road layers
export const getRoadLayersFromStyle = (map) => {
  // Get all road-related layers from your custom style
  const style = map.getStyle();
  const roadLayers = [];
  
  if (style && style.layers) {
    style.layers.forEach(layer => {
      // Look for road-related layer names (customize these based on your style)
      if (layer.id.includes('road') || 
          layer.id.includes('path') || 
          layer.id.includes('way') ||
          layer.type === 'line') {
        roadLayers.push(layer);
      }
    });
  }
  
  return roadLayers;
};

// Query features from your custom style's road layers
export const queryRoadFeatures = (map, coordinates, radius = 50) => {
  const roadLayers = getRoadLayersFromStyle(map);
  const features = [];
  
  roadLayers.forEach(layer => {
    try {
      const queryResult = map.queryRenderedFeatures(
        [[coordinates[0] - 0.001, coordinates[1] - 0.001], 
         [coordinates[0] + 0.001, coordinates[1] + 0.001]], 
        { layers: [layer.id] }
      );
      features.push(...queryResult);
    } catch (error) {
      console.warn(`Could not query layer ${layer.id}:`, error);
    }
  });
  
  return features;
};
